import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { PerformanceSummaryWidget } from '../components/widgets/PerformanceSummaryWidget';
import { GoalsProgressWidget } from '../components/widgets/GoalsProgressWidget';
import { PIPsWidget } from '../components/widgets/PIPsWidget';
import { IDPsWidget } from '../components/widgets/IDPsWidget';
import { FeedbacksReceivedWidget } from '../components/widgets/FeedbacksReceivedWidget';
import { useAnalyticsData } from '../hooks/useAnalyticsData';
import { useLearnBackendData } from '../hooks/useLearnBackendData';
import { usePendingItemsData } from '../hooks/usePendingItemsData';
import { useCycleOptions } from '../hooks/useCycleOptions';
import { LearningPlanCard } from '@t-one-internal/design-system//lib/components/widgets/my-portal/';
import { PendingItemsWidget } from '../components/widgets/PendingItemsWidget';
import { LearnCalendarWidget } from '../components/widgets/LearnCalendarWidget';
import { PerformSparkCalendarWidget } from '../components/widgets/PerformSparkCalendarWidget';
import { useSubscriptionStore } from '../stores/subscriptionStore';
import { useAuth } from '../context/AuthContextBackend';
import {
    MyApprovalsWidget,
    MyCollaborationsWidget,
    JDQuickStatsWidget,
    LatestTitlesWidget,
    JobFamilyDistWidget,
    WorkflowStatusWidget,
    CollaborationOverviewWidget,
    TopApproversWidget,
    JobArchitectureWidget,
    useCompBldrMyData,
} from '../components/widgets/CompBldrWidgets';
import {
    MyActiveCoursesWidget,
    ContinueLearningWidget,
    UpcomingDeadlinesWidget,
    CertificatesEarnedWidget,
    MyLearningActivityWidget,
    SkillDevelopmentWidget,
    MyUpcomingSessionsWidget,
    MyCoachingJourneyWidget,
} from '../components/widgets/learn';

type PortalView = 'perform' | 'learn';

// Fixed widget configuration for My Portal
const WIDGET_CONFIG = {
    performance_summary: { metricKey: 'mp_performance_summary', title: 'Current Performance Summary' },
    goals_progress: { metricKey: 'mp_goals_progress_list', title: 'Goals' },
    pips: { metricKey: 'mp_pips_list', title: 'PIPs' },
    idps: { metricKey: 'mp_idps_list', title: 'IDPs' },
    feedbacks: { metricKey: 'mp_feedbacks_received', title: 'Feedbacks Received' },
    learning_plan: { metricKey: 'learn_api:learning_plan_enrollments', title: 'My Learning Plan' },
};

// Learn widget metric keys (matching Trainery-Analytics registry_learn.go)
const LEARN_WIDGET_CONFIG = {
    active_courses: { metricKey: 'learn_my_active_courses', title: 'My Active Courses' },
    certificates: { metricKey: 'learn_my_certificates', title: 'Certificates Earned' },
    activity: { metricKey: 'learn_my_activity_summary', title: 'My Learning Activity' },
    skills: { metricKey: 'learn_my_skill_development', title: 'Skill Development' },
    sessions: { metricKey: 'learn_my_upcoming_sessions', title: 'My Upcoming Sessions' },
    coaching: { metricKey: 'learn_my_coaching', title: 'My Coaching Journey' },
    coaching_goals: { metricKey: 'learn_my_coaching_goals', title: 'My Coaching Goals' },
};

// Shared spinner component
const WidgetSpinner: React.FC = () => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <div
            style={{
                width: '24px',
                height: '24px',
                border: '3px solid #E5E7EB',
                borderTopColor: '#4B45E5',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
            }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
);

// ---- View Toggle Switcher ----

interface ViewToggleProps {
    activeView: PortalView;
    onToggle: (view: PortalView) => void;
}

const ViewToggle: React.FC<ViewToggleProps> = ({ activeView, onToggle }) => (
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                backgroundColor: '#F3F4F6',
                borderRadius: '10px',
                padding: '3px',
            }}
        >
            <button
                onClick={() => onToggle('perform')}
                style={{
                    padding: '6px 16px',
                    fontSize: '13px',
                    fontWeight: activeView === 'perform' ? '600' : '500',
                    color: activeView === 'perform' ? '#4B45E5' : '#6B7280',
                    backgroundColor: activeView === 'perform' ? '#FFFFFF' : 'transparent',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: activeView === 'perform' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                    whiteSpace: 'nowrap',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                }}
            >
                PerformSpark
            </button>
            <button
                onClick={() => onToggle('learn')}
                style={{
                    padding: '6px 16px',
                    fontSize: '13px',
                    fontWeight: activeView === 'learn' ? '600' : '500',
                    color: activeView === 'learn' ? '#4B45E5' : '#6B7280',
                    backgroundColor: activeView === 'learn' ? '#FFFFFF' : 'transparent',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: activeView === 'learn' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                    whiteSpace: 'nowrap',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                }}
            >
                TraineryLearn
            </button>
        </div>
    </div>
);

// ---- Main Page ----

const MyPortalPage: React.FC = () => {
    // Subscription check — only show module widgets if subscribed AND active (not expired)
    const { user } = useAuth();
    const isSubscriptionLoading = useSubscriptionStore((s) => s.isLoading);
    const isModuleEnabled = useSubscriptionStore((s) => s.isModuleEnabled);
    const isModuleActive = useSubscriptionStore((s) => s.isModuleActive);
    const isSubmoduleEnabled = useSubscriptionStore((s) => s.isSubmoduleEnabled);
    const isCustomerUser = (user as any)?.user_category === 'customer_employee';
    const hasLearn = isSubscriptionLoading || isCustomerUser || (isModuleEnabled('learn') && isModuleActive('learn'));
    const hasPerform = isSubscriptionLoading || isCustomerUser || (isModuleEnabled('perform-spark') && isModuleActive('perform-spark'));
    const hasCompBldr = isSubscriptionLoading || isCustomerUser || (isModuleEnabled('comp-builder') && isModuleActive('comp-builder'));

    // Submodule-level flags — each widget should only render when its specific
    // submodule is enabled. Loading state and customer employees bypass the check
    // to mirror the module-level behaviour above. Each flag also requires the
    // parent module flag so an expired module hides its submodules cleanly.
    const hasGoals = isSubscriptionLoading || isCustomerUser || (hasPerform && isSubmoduleEnabled('review-cloud', 'goal-management'));
    const hasPerformanceMgmt = isSubscriptionLoading || isCustomerUser || (hasPerform && isSubmoduleEnabled('review-cloud', 'performance-management'));
    const hasLms = isSubscriptionLoading || isCustomerUser || (hasLearn && isSubmoduleEnabled('learn', 'lms'));
    const hasTms = isSubscriptionLoading || isCustomerUser || (hasLearn && isSubmoduleEnabled('learn', 'training-management'));
    const hasCoaching = isSubscriptionLoading || isCustomerUser || (hasLearn && isSubmoduleEnabled('learn', 'coaching'));

    // Determine default view based on subscriptions
    const defaultView: PortalView = hasPerform ? 'perform' : hasLearn ? 'learn' : 'perform';
    const [activeView, setActiveView] = useState<PortalView>(defaultView);

    // Sync active view when subscription data loads
    useEffect(() => {
        if (!isSubscriptionLoading) {
            if (!hasPerform && hasLearn) {
                setActiveView('learn');
            } else if (hasPerform && !hasLearn) {
                setActiveView('perform');
            }
        }
    }, [isSubscriptionLoading, hasPerform, hasLearn]);

    // Pending items filter state (lifted so the hook re-fetches on filter change)
    const [pendingRoleFilter, setPendingRoleFilter] = useState<string>('');
    const [pendingTaskTypeFilter, setPendingTaskTypeFilter] = useState<string>('');

    // Cycle selection state
    const [selectedReviewCycleId, setSelectedReviewCycleId] = useState<string>('');
    const [selectedGoalCycleId, setSelectedGoalCycleId] = useState<string>('');

    // Fetch cycle options for dropdowns
    const { options: reviewCycleOptions, loading: reviewCyclesLoading } = useCycleOptions('standard_review_cycle_id');
    const { options: goalCycleOptions, loading: goalCyclesLoading } = useCycleOptions('goal_cycle_id');

    // Auto-select first cycle when options load
    useEffect(() => {
        if (reviewCycleOptions.length > 0 && !selectedReviewCycleId) {
            setSelectedReviewCycleId(reviewCycleOptions[0].value);
        }
    }, [reviewCycleOptions]);

    useEffect(() => {
        if (goalCycleOptions.length > 0 && !selectedGoalCycleId) {
            setSelectedGoalCycleId(goalCycleOptions[0].value);
        }
    }, [goalCycleOptions]);

    const handlePendingRoleChange = useCallback((role: string) => setPendingRoleFilter(role), []);
    const handlePendingTaskTypeChange = useCallback((taskType: string) => setPendingTaskTypeFilter(taskType), []);
    const handleReviewCycleChange = useCallback((cycleId: string) => setSelectedReviewCycleId(cycleId), []);
    const handleGoalCycleChange = useCallback((cycleId: string) => setSelectedGoalCycleId(cycleId), []);

    // Build filters for cycle-dependent widgets
    const performanceFilters = useMemo(
        () => selectedReviewCycleId ? [{ field: 'standard_review_cycle_id', operator: '=', value: selectedReviewCycleId }] : [],
        [selectedReviewCycleId]
    );
    const goalsFilters = useMemo(
        () => selectedGoalCycleId ? [{ field: 'goal_cycle_id', operator: '=', value: selectedGoalCycleId }] : [],
        [selectedGoalCycleId]
    );

    // Fetch pending items from Events API — always fetch (common for all modules)
    const { data: pendingItemsData, loading: pendingItemsLoading, error: pendingItemsError } = usePendingItemsData({
        assigneeRole: pendingRoleFilter || undefined,
        taskType: pendingTaskTypeFilter || undefined,
    });

    // Fetch data for each widget — gated at submodule level so we don't issue
    // requests for widgets the user can't see anyway. Each metric_key maps 1:1
    // to a tagged metric in the analytics backend (see subscription_tags.go),
    // so the FE flag here mirrors the backend gate.
    const { data: performanceData, loading: performanceLoading, error: performanceError } = useAnalyticsData({
        metricKey: WIDGET_CONFIG.performance_summary.metricKey,
        filters: performanceFilters,
        scoping: 'self',
        enabled: hasPerformanceMgmt,
    });

    const { data: goalsData, loading: goalsLoading, error: goalsError } = useAnalyticsData({
        metricKey: WIDGET_CONFIG.goals_progress.metricKey,
        filters: goalsFilters,
        scoping: 'self',
        enabled: hasGoals,
    });

    const { data: pipsData, loading: pipsLoading, error: pipsError } = useAnalyticsData({
        metricKey: WIDGET_CONFIG.pips.metricKey,
        scoping: 'self',
        enabled: hasPerformanceMgmt,
    });

    const { data: idpsData, loading: idpsLoading, error: idpsError } = useAnalyticsData({
        metricKey: WIDGET_CONFIG.idps.metricKey,
        scoping: 'self',
        enabled: hasPerformanceMgmt,
    });

    const { data: feedbacksData, loading: feedbacksLoading, error: feedbacksError } = useAnalyticsData({
        metricKey: WIDGET_CONFIG.feedbacks.metricKey,
        scoping: 'self',
        enabled: hasPerformanceMgmt,
    });

    // Fetch learning plan from Learn API — only if LMS submodule is enabled
    const { data: learningPlanData, loading: learningPlanLoading, error: learningPlanError } = useLearnBackendData({
        templateKey: 'mp_learning_plan',
        metricKey: WIDGET_CONFIG.learning_plan.metricKey,
        chartType: 'learning_plan_card',
        enabled: hasLms,
    });

    // ---- Trainery Learn widgets (via Analytics API) — gated per submodule ----
    const { data: activeCoursesData, loading: activeCoursesLoading, error: activeCoursesError } = useAnalyticsData({
        metricKey: LEARN_WIDGET_CONFIG.active_courses.metricKey,
        scoping: 'self',
        enabled: hasLms,
    });

    const { data: certificatesData, loading: certificatesLoading, error: certificatesError } = useAnalyticsData({
        metricKey: LEARN_WIDGET_CONFIG.certificates.metricKey,
        scoping: 'self',
        enabled: hasLms,
    });

    const { data: activityData, loading: activityLoading, error: activityError } = useAnalyticsData({
        metricKey: LEARN_WIDGET_CONFIG.activity.metricKey,
        scoping: 'self',
        enabled: hasLms,
    });

    const { data: skillsData, loading: skillsLoading, error: skillsError } = useAnalyticsData({
        metricKey: LEARN_WIDGET_CONFIG.skills.metricKey,
        scoping: 'self',
        enabled: hasLms,
    });

    const { data: sessionsData, loading: sessionsLoading, error: sessionsError } = useAnalyticsData({
        metricKey: LEARN_WIDGET_CONFIG.sessions.metricKey,
        scoping: 'self',
        enabled: hasTms,
    });

    const { data: coachingData, loading: coachingLoading, error: coachingError } = useAnalyticsData({
        metricKey: LEARN_WIDGET_CONFIG.coaching.metricKey,
        scoping: 'self',
        enabled: hasCoaching,
    });

    const { data: coachingGoalsData, loading: coachingGoalsLoading } = useAnalyticsData({
        metricKey: LEARN_WIDGET_CONFIG.coaching_goals.metricKey,
        scoping: 'self',
        enabled: hasCoaching,
    });

    // ---- CompBldr widgets (Job Builder) — only if comp-builder subscribed ----
    const {
        approvals: myApprovals, approvalsLoading: myApprovalsLoading, approvalsError: myApprovalsError,
        collaborations: myCollaborations, collaborationsLoading: myCollaborationsLoading, collaborationsError: myCollaborationsError,
        summary: compSummary, summaryLoading: compSummaryLoading, summaryError: compSummaryError,
        topApprovers, topApproversLoading, topApproversError,
    } = useCompBldrMyData(hasCompBldr);

    // ---- Build flat widget arrays per view ----
    // Widgets flow into a 2-column CSS grid as a flat array. When a widget is
    // hidden (unsubscribed), subsequent widgets shift up — no empty gaps, no
    // size changes.

    const widgetCard = (key: string, node: React.ReactNode, extraClass?: string) => (
        <div key={key} className={`bg-white border border-gray-200 rounded-xl ${extraClass || 'overflow-hidden'}`} style={{ height: '320px' }}>
            {node}
        </div>
    );

    // ---- PerformSpark view ----
    const performWidgets: React.ReactNode[] = [
        widgetCard('pending', (
            <PendingItemsWidget
                data={pendingItemsData}
                loading={pendingItemsLoading}
                error={pendingItemsError || undefined}
                onRoleFilterChange={handlePendingRoleChange}
                onTaskTypeFilterChange={handlePendingTaskTypeChange}
            />
        )),
        widgetCard('calendar-ps', <PerformSparkCalendarWidget />, 'overflow-visible'),
    ];
    if (hasGoals) {
        performWidgets.push(widgetCard('goals', (
            <GoalsProgressWidget
                goals={goalsData}
                loading={goalsLoading}
                error={goalsError || undefined}
                cycleOptions={goalCycleOptions}
                selectedCycleId={selectedGoalCycleId}
                onCycleChange={handleGoalCycleChange}
                cycleOptionsLoading={goalCyclesLoading}
            />
        )));
    }
    if (hasPerformanceMgmt) {
        performWidgets.push(widgetCard('perf-summary', (
            <PerformanceSummaryWidget
                data={performanceData}
                loading={performanceLoading}
                error={performanceError || undefined}
                cycleOptions={reviewCycleOptions}
                selectedCycleId={selectedReviewCycleId}
                onCycleChange={handleReviewCycleChange}
                cycleOptionsLoading={reviewCyclesLoading}
            />
        )));
    }
    if (hasLms) {
        performWidgets.push(widgetCard('learning-plan', (
            <>
                <div className="px-4 pt-4 pb-2">
                    <h3 className="text-base font-semibold text-gray-800">{WIDGET_CONFIG.learning_plan.title}</h3>
                </div>
                {learningPlanLoading ? (
                    <WidgetSpinner />
                ) : learningPlanError ? (
                    <div className="flex items-center justify-center flex-1 text-red-500 text-sm p-4">
                        {learningPlanError}
                    </div>
                ) : (
                    <div className="flex-1 overflow-auto">
                        <LearningPlanCard data={learningPlanData} options={{ max_items: 5 }} />
                    </div>
                )}
            </>
        ), 'overflow-hidden flex flex-col'));
    }
    if (hasPerformanceMgmt) {
        performWidgets.push(
            widgetCard('idps', <IDPsWidget idps={idpsData} loading={idpsLoading} error={idpsError || undefined} />),
            widgetCard('feedbacks', <FeedbacksReceivedWidget feedbacks={feedbacksData} loading={feedbacksLoading} error={feedbacksError || undefined} />),
        );
        if (!pipsLoading && Array.isArray(pipsData) && pipsData.length > 0) {
            performWidgets.push(widgetCard('pips', <PIPsWidget pips={pipsData} loading={pipsLoading} error={pipsError || undefined} />));
        }
    }
    if (hasCompBldr) {
        performWidgets.push(
            widgetCard('job-family', <JobFamilyDistWidget families={compSummary?.jobFamilySummary} loading={compSummaryLoading} error={compSummaryError || undefined} />),
            widgetCard('collab-overview', <CollaborationOverviewWidget summary={compSummary} loading={compSummaryLoading} error={compSummaryError || undefined} />),
            widgetCard('job-arch', <JobArchitectureWidget summary={compSummary} loading={compSummaryLoading} error={compSummaryError || undefined} />),
            widgetCard('top-approvers', <TopApproversWidget data={topApprovers} loading={topApproversLoading} error={topApproversError || undefined} />),
            widgetCard('my-approvals', <MyApprovalsWidget data={myApprovals} loading={myApprovalsLoading} error={myApprovalsError || undefined} />),
            widgetCard('latest-titles', <LatestTitlesWidget titles={compSummary?.latestJds} loading={compSummaryLoading} error={compSummaryError || undefined} />),
            widgetCard('jd-stats', <JDQuickStatsWidget summary={compSummary} loading={compSummaryLoading} error={compSummaryError || undefined} />),
            widgetCard('workflow', <WorkflowStatusWidget summary={compSummary} loading={compSummaryLoading} error={compSummaryError || undefined} />),
        );
    }

    // ---- Trainery Learn view ----
    const learnWidgets: React.ReactNode[] = [];
    if (hasLms) {
        learnWidgets.push(widgetCard('active-courses', (
            <MyActiveCoursesWidget data={activeCoursesData} loading={activeCoursesLoading} error={activeCoursesError || undefined} />
        )));
    }
    learnWidgets.push(widgetCard('calendar-learn', <LearnCalendarWidget />, 'overflow-visible'));
    if (hasLms) {
        learnWidgets.push(
            widgetCard('continue-learning', <ContinueLearningWidget data={activeCoursesData} loading={activeCoursesLoading} error={activeCoursesError || undefined} />),
            widgetCard('upcoming-deadlines', <UpcomingDeadlinesWidget data={activeCoursesData} loading={activeCoursesLoading} error={activeCoursesError || undefined} />),
            widgetCard('certificates', <CertificatesEarnedWidget data={certificatesData} loading={certificatesLoading} error={certificatesError || undefined} />),
            widgetCard('activity', <MyLearningActivityWidget data={activityData} loading={activityLoading} error={activityError || undefined} />),
            widgetCard('skills', <SkillDevelopmentWidget data={skillsData} loading={skillsLoading} error={skillsError || undefined} />),
        );
    }
    if (hasTms) {
        learnWidgets.push(widgetCard('sessions', <MyUpcomingSessionsWidget data={sessionsData} loading={sessionsLoading} error={sessionsError || undefined} />));
    }
    if (hasCoaching) {
        learnWidgets.push(widgetCard('coaching', (
            <MyCoachingJourneyWidget data={coachingData} goalsData={coachingGoalsData} loading={coachingLoading} goalsLoading={coachingGoalsLoading} error={coachingError || undefined} />
        )));
    }

    // ---- No-module fallback view ----
    const noModuleWidgets: React.ReactNode[] = [
        widgetCard('pending', (
            <PendingItemsWidget
                data={pendingItemsData}
                loading={pendingItemsLoading}
                error={pendingItemsError || undefined}
                onRoleFilterChange={handlePendingRoleChange}
                onTaskTypeFilterChange={handlePendingTaskTypeChange}
            />
        )),
        widgetCard('calendar', <LearnCalendarWidget />, 'overflow-visible'),
    ];
    if (hasCompBldr) {
        noModuleWidgets.push(
            widgetCard('job-family', <JobFamilyDistWidget families={compSummary?.jobFamilySummary} loading={compSummaryLoading} error={compSummaryError || undefined} />),
            widgetCard('collab-overview', <CollaborationOverviewWidget summary={compSummary} loading={compSummaryLoading} error={compSummaryError || undefined} />),
            widgetCard('job-arch', <JobArchitectureWidget summary={compSummary} loading={compSummaryLoading} error={compSummaryError || undefined} />),
            widgetCard('top-approvers', <TopApproversWidget data={topApprovers} loading={topApproversLoading} error={topApproversError || undefined} />),
            widgetCard('my-approvals', <MyApprovalsWidget data={myApprovals} loading={myApprovalsLoading} error={myApprovalsError || undefined} />),
            widgetCard('my-collabs', <MyCollaborationsWidget data={myCollaborations} loading={myCollaborationsLoading} error={myCollaborationsError || undefined} />),
            widgetCard('latest-titles', <LatestTitlesWidget titles={compSummary?.latestJds} loading={compSummaryLoading} error={compSummaryError || undefined} />),
            widgetCard('jd-stats', <JDQuickStatsWidget summary={compSummary} loading={compSummaryLoading} error={compSummaryError || undefined} />),
            widgetCard('workflow', <WorkflowStatusWidget summary={compSummary} loading={compSummaryLoading} error={compSummaryError || undefined} />),
        );
    }

    // Select widget set based on subscriptions and active view
    const activeWidgets = !hasPerform && !hasLearn
        ? noModuleWidgets
        : (hasPerform && (activeView === 'perform' || !hasLearn))
            ? performWidgets
            : hasLearn
                ? learnWidgets
                : [];

    return (
        <div className="flex flex-col h-full bg-white relative">
            <div className="flex-1 overflow-auto p-6 bg-white">
                {hasPerform && hasLearn && <ViewToggle activeView={activeView} onToggle={setActiveView} />}
                <div className="grid grid-cols-2 gap-5">
                    {activeWidgets}
                </div>
            </div>
        </div>
    );
};

export default MyPortalPage;
